import { z } from "zod";
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Modal, Button, Form, Row, Col, InputGroup } from "react-bootstrap";
import useGetAreasHook from "activity/sub_modules/priority/hooks/useGetAreas.hook";
import Select from "react-select";
import { DmcMasterApi } from "api/dmcMaster.api";
import { toast } from "react-toastify";
import { areasTransferApi } from "../api/AreasTransfer.api";
import ActivityLoader from "activity/componenets/ActivityLoader";
import {
  IAreasTransfers,
  ISharingBasisEnum,
  TransferTypeEnum,
} from "commonFnBcode/types/dmc.types";
import useFetchTourOptions from "../hooks/useFetchTourOptions";

export const transferSchema = z
  .object({
    from: z.number({ required_error: "From area is required" }),
    to: z.number({ required_error: "To area is required" }),
    marketId: z.number({ required_error: "Market ID is required" }),
    description: z.string().optional(),
    transferType: z.nativeEnum(TransferTypeEnum),
    sharingBasis: z.nativeEnum(ISharingBasisEnum),
    amount: z.number().min(0, "Amount must be positive"),
    isStandAlone: z.boolean(),
    isPerPax: z.boolean(),
    minPax: z.coerce.number().optional(),
    boundTourOptionId: z.number().optional(),
    maxPax: z.coerce.number().optional(),
    validFromDate: z.string().refine((val) => !isNaN(Date.parse(val)), {
      message: "Invalid date format",
    }),
    validTillDate: z.string().refine((val) => !isNaN(Date.parse(val)), {
      message: "Invalid date format",
    }),
  })
  .superRefine((data, ctx) => {
    if (
      !data.minPax &&
      !data.maxPax &&
      data.sharingBasis === ISharingBasisEnum.private
    ) {
      ctx.addIssue({
        path: ["sharingBasis"],
        code: z.ZodIssueCode.custom,
        message: "Please Enter min and max pax for private sharing basis",
      });
    }
  });

export type TransferFormData = z.infer<typeof transferSchema>;

const AddAreaTransferModal = ({
  defaultValue,
  show,
  onHide,
}: {
  defaultValue?: IAreasTransfers;
  show: boolean;
  onHide: () => void;
}) => {
  const [submitTransfer, { isLoading }] =
    areasTransferApi.useAddAreaTransferMutation();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<TransferFormData>({
    resolver: zodResolver(transferSchema),
    defaultValues: {
      isPerPax: true,
      isStandAlone: false,
      boundTourOptionId: -1,
      sharingBasis: ISharingBasisEnum.sharing,
    },
  });

  const sharingBasis = watch("sharingBasis");

  React.useEffect(() => {
    setValue("isPerPax", sharingBasis === ISharingBasisEnum.sharing);
  }, [sharingBasis]);

  const onSubmit = (data: TransferFormData) => {
    submitTransfer(data);
    onHide();
  };

  const { area: toArea, setArea: setToArea, data: toData } = useGetAreasHook();
  const {
    area: fromArea,
    setArea: setFromArea,
    data: fromData,
  } = useGetAreasHook();

  const [markets, setMarkets] = React.useState<any>([]);
  const searchMarket = async () => {
    const api = new DmcMasterApi();
    const response = await api.searchMarket({ name: "" });
    if (!response.success)
      return toast.error(response.errorMessage || "Failed to fetch markets...");

    setMarkets(
      response.result.map((market) => ({ label: market.name, value: market }))
    );
  };
  React.useEffect((): any => searchMarket(), []);
  React.useEffect(() => {
    if (defaultValue) {
      setValue("from", defaultValue.from.id);
      setValue("to", defaultValue.to.id);
      setValue("description", defaultValue?.description || "");
      setValue("transferType", defaultValue.transferType);
      setValue("sharingBasis", defaultValue.sharingBasis);
      setValue("amount", defaultValue.amount);
      setValue("isStandAlone", defaultValue.isStandAlone);
      setValue("isPerPax", defaultValue.isPerPax);
      setValue("minPax", defaultValue.minPax);
      setValue("maxPax", defaultValue.maxPax);
      setValue("validFromDate", defaultValue.validFromDate as any);
      setValue("validTillDate", defaultValue.validTillDate as any);
      setValue("marketId", defaultValue.marketId);
    }
  }, [defaultValue]);

  const {
    setRegex: setBoundTourRegex,
    regex: boundTourRegex,
    tourOptions,
  } = useFetchTourOptions();

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <i className="bi bi-truck me-2"></i>
          Add New Area Transfer
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-light">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">From Area</Form.Label>
                <Select
                  className="w-100"
                  onInputChange={(val) => setFromArea(val)}
                  defaultValue={{
                    label: defaultValue?.from?.name || "Select Area",
                    value: defaultValue?.from?.id || 0,
                  }}
                  onChange={(selectedOption) => {
                    setValue("from", selectedOption?.value || 0, {
                      shouldValidate: true,
                    });
                  }}
                  inputValue={fromArea}
                  placeholder="Search Areas"
                  options={fromData}
                />
                {errors.from && (
                  <div className="invalid-feedback d-block">
                    {errors.from.message}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">To Area</Form.Label>
                <Select
                  className="w-100"
                  onInputChange={(val) => setToArea(val)}
                  defaultValue={{
                    label: defaultValue?.to?.name || "Select Area",
                    value: defaultValue?.to?.id || 0,
                  }}
                  onChange={(selectedOption) => {
                    setValue("to", selectedOption?.value || 0, {
                      shouldValidate: true,
                    });
                  }}
                  inputValue={toArea}
                  placeholder="Search Areas"
                  options={toData}
                />
                {errors.to && (
                  <div className="invalid-feedback d-block">
                    {errors.to?.message}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">Market ID</Form.Label>
                <Select
                  className="w-100"
                  options={markets}
                  placeholder="Select Market"
                  value={markets.find(
                    (market) => market.value.id === watch("marketId")
                  )}
                  onChange={(selectedOption) => {
                    setValue("marketId", selectedOption?.value?.id, {
                      shouldValidate: true,
                    });
                  }}
                />
                {errors.marketId && (
                  <div className="invalid-feedback d-block">
                    {errors.marketId.message}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">Description</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter description"
                  {...register("description")}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-bold">Transfer Type</Form.Label>
                <Form.Select
                  {...register("transferType")}
                  isInvalid={!!errors.transferType}
                >
                  {Object.values(TransferTypeEnum).map((transferType) => (
                    <option key={transferType} value={transferType}>
                      {transferType}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.transferType?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-bold">Sharing Basis</Form.Label>
                <Form.Select
                  {...register("sharingBasis")}
                  isInvalid={!!errors.sharingBasis}
                >
                  {Object.values(ISharingBasisEnum).map((share) => (
                    <option key={share} value={share}>
                      {share}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.sharingBasis?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-bold">
                  WhiteList Tour Option
                </Form.Label>

                <Select
                  className="w-100"
                  onInputChange={(val) => setBoundTourRegex(val)}
                  isClearable
                  defaultValue={{
                    label: "Please Select Tour Option",
                    value: -1,
                  }}
                  onChange={(selectedOption) => {
                    setValue("boundTourOptionId", selectedOption?.value || -1, {
                      shouldValidate: true,
                    });
                  }}
                  inputValue={boundTourRegex}
                  placeholder="Search Areas"
                  options={tourOptions}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.boundTourOptionId?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">
                  Amount
                  {sharingBasis === ISharingBasisEnum.sharing && " - Per Pax"}
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    {...register("amount", { valueAsNumber: true })}
                    isInvalid={!!errors.amount}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.amount?.message}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6} className="d-flex align-items-center">
              <Form.Group className="mt-4">
                <Form.Check
                  type="switch"
                  id="isStandAlone"
                  label="Is Standalone"
                  {...register("isStandAlone")}
                />
                <h6>Transfers can be sold alone and with activity </h6>
              </Form.Group>
              <Form.Group>
                <Form.Check
                  type="switch"
                  id="isPerPax"
                  label="Is Per Pax"
                  style={{ display: "none" }}
                  {...register("isPerPax")}
                />
              </Form.Group>
            </Col>
          </Row>

          {sharingBasis === ISharingBasisEnum.private && (
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Min Pax</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Minimum passengers"
                    {...register("minPax", { valueAsNumber: true })}
                    isInvalid={!!errors.minPax}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.minPax?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Max Pax</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Maximum passengers"
                    {...register("maxPax", { valueAsNumber: true })}
                    isInvalid={!!errors.maxPax}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.maxPax?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          )}

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">Valid From Date</Form.Label>
                <Form.Control
                  type="date"
                  {...register("validFromDate")}
                  isInvalid={!!errors.validFromDate}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.validFromDate?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">Valid Till Date</Form.Label>
                <Form.Control
                  type="date"
                  {...register("validTillDate")}
                  isInvalid={!!errors.validTillDate}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.validTillDate?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {isLoading ? (
            <ActivityLoader />
          ) : (
            <div className="d-flex justify-content-end mt-4">
              <Button
                variant="secondary"
                className="me-2 btn-flat"
                onClick={onHide}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                className="btn-flat"
                type="submit"
                disabled={isSubmitting}
                onClick={handleSubmit(onSubmit)}
              >
                {isSubmitting ? "Saving..." : "Save Area Transfer"}
              </Button>
            </div>
          )}
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AddAreaTransferModal;
